import {
  pgTable,
  text,
  timestamp,
  uuid,
  integer,
  real,
  jsonb,
  index,
  uniqueIndex,
} from "drizzle-orm/pg-core";

// D07: FK constraints added. tenantId excluded (partition key in per-tenant DB, not a FK).
import { sentEmails } from "./email";

// ---------------------------------------------------------------------------
// Status enums
// ---------------------------------------------------------------------------

export const TENANT_EMAIL_STATUSES = [
  "pending_domain",
  "verifying_dkim",
  "active",
  "suspended",
] as const;

export type TenantEmailStatus = (typeof TENANT_EMAIL_STATUSES)[number];

// ---------------------------------------------------------------------------
// Timestamp helper
// ---------------------------------------------------------------------------

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Tables
// ---------------------------------------------------------------------------

export const tenantEmailConfig = pgTable("tenant_email_config", {
  tenantId: uuid("tenant_id").primaryKey(),
  status: text("status").notNull().default("pending_domain"),
  sesConfigurationSetName: text("ses_configuration_set_name"),
  sesRegion: text("ses_region").notNull().default("eu-west-1"),
  sesSecondaryRegion: text("ses_secondary_region").default("eu-central-1"),
  sendingDomain: text("sending_domain"),
  transactionalSubdomain: text("transactional_subdomain"),
  marketingSubdomain: text("marketing_subdomain"),
  bounceSubdomain: text("bounce_subdomain"),
  trackingDomain: text("tracking_domain"),
  dkimTokens: jsonb("dkim_tokens").$type<string[]>(),
  dkimVerifiedAt: timestamp("dkim_verified_at", tz),
  spfVerifiedAt: timestamp("spf_verified_at", tz),
  dmarcVerifiedAt: timestamp("dmarc_verified_at", tz),
  dailySendLimit: integer("daily_send_limit").notNull().default(100),
  currentTier: text("current_tier").notNull().default("starter"),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
});

export const tenantSendingReputation = pgTable("tenant_sending_reputation", {
  tenantId: uuid("tenant_id").primaryKey(),
  reputationScore: real("reputation_score").notNull().default(50),
  complaints30dRate: real("complaints_30d_rate").notNull().default(0),
  bounces30dRate: real("bounces_30d_rate").notNull().default(0),
  engagement30dRate: real("engagement_30d_rate").notNull().default(0),
  lastReviewedAt: timestamp("last_reviewed_at", tz),
  reviewedBy: text("reviewed_by"),
  tierChangedAt: timestamp("tier_changed_at", tz),
  tierChangeReason: text("tier_change_reason"),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
});

// ---------------------------------------------------------------------------
// EM-I: DKIM key rotation history
// Tracks every rotation event — selector, public-key DNS record, status,
// 7-day grace period, retirement. Old keys MUST stay active until grace ends
// so in-flight mail signed with the previous selector still validates.
// ---------------------------------------------------------------------------

export const DKIM_ROTATION_STATUSES = ["active", "grace", "retired"] as const;
export type DkimRotationStatus = (typeof DKIM_ROTATION_STATUSES)[number];

export const dkimKeyRotations = pgTable(
  "dkim_key_rotations",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    selector: text("selector").notNull(),
    publicKey: text("public_key").notNull(),
    dnsRecordName: text("dns_record_name").notNull(),
    dnsRecordValue: text("dns_record_value").notNull(),
    status: text("status").notNull().default("active"),
    publishedAt: timestamp("published_at", tz).notNull().defaultNow(),
    graceUntil: timestamp("grace_until", tz),
    retiredAt: timestamp("retired_at", tz),
    rotatedBy: uuid("rotated_by"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("idx_dkim_rotations_tenant_selector").on(
      table.tenantId,
      table.selector,
    ),
    index("idx_dkim_rotations_tenant_status").on(table.tenantId, table.status),
    index("idx_dkim_rotations_grace_until").on(table.graceUntil),
  ],
);

export const emailSendLog = pgTable(
  "email_send_log",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    correlationId: text("correlation_id")
      .notNull()
      .references(() => sentEmails.correlationId, { onDelete: "cascade" }),
    enqueuedAt: timestamp("enqueued_at", tz).notNull().defaultNow(),
    dequeuedAt: timestamp("dequeued_at", tz),
    renderingDurationMs: integer("rendering_duration_ms"),
    sesMessageId: text("ses_message_id"),
    sesRegion: text("ses_region"),
    deliveryStatus: text("delivery_status"),
    error: text("error"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("idx_email_send_log_correlation").on(table.correlationId),
    index("idx_email_send_log_tenant").on(table.tenantId),
  ],
);
